import { HeadContent, Link, Scripts, createRootRoute, useLocation } from '@tanstack/react-router'
import type { CSSProperties, ReactNode } from 'react'
import { LampMark } from '../components/LampMark'
import { assetUrl } from '../lib/assets'
import { SiteHeader } from '../components/SiteHeader'
import { useFocusChime } from '../components/cafe/chime'
import { localeFromPathname, useLocale } from '../components/use-locale'
import { type Locale, loadDict } from '../i18n'
import css from '../styles/global.css?url'

const FONTS =
  'https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400..700;1,6..96,400..600&family=DM+Mono:wght@400;500&family=Shippori+Mincho+B1:wght@500;600;800&display=swap'

// キリル文字とベトナム語は Bodoni Moda にないので、その言語のページだけ Playfair Display を足す
const FONTS_EXTENDED = `${FONTS}&family=Playfair+Display:ital,wght@0,400..700;1,400..600`
// タイ文字とデーヴァナーガリーは、その文字を持つ明朝系の Noto Serif で補う
const FONTS_THAI = `${FONTS}&family=Noto+Serif+Thai:wght@400..700`
const FONTS_DEVANAGARI = `${FONTS}&family=Noto+Serif+Devanagari:wght@400..700`

function fontsFor(locale: Locale): string {
  if (locale === 'ru' || locale === 'vi') return FONTS_EXTENDED
  if (locale === 'th') return FONTS_THAI
  if (locale === 'hi') return FONTS_DEVANAGARI
  return FONTS
}

// 最初の描画と回線を取り合わないよう、文字が最初に描かれてから足す（測れないブラウザでは読み込み後に足す）
function fontLoader(href: string): string {
  return `(function(){var d=0;function a(){if(d)return;d=1;var l=document.createElement('link');l.rel='stylesheet';l.href=${JSON.stringify(href)};document.head.appendChild(l)}try{new PerformanceObserver(function(s){if(s.getEntriesByName('first-contentful-paint').length)a()}).observe({type:'paint',buffered:true})}catch(e){}addEventListener('load',function(){setTimeout(a,1000)})})()`
}

export const Route = createRootRoute({
  // 言語をまたいで移るときは、描く前にその言語の辞書を読み込む
  beforeLoad: ({ location }) => loadDict(localeFromPathname(location.pathname)),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
      { name: 'theme-color', content: '#070a12' },
      { name: 'color-scheme', content: 'dark' },
    ],
    links: [
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      { rel: 'stylesheet', href: css },
    ],
  }),
  shellComponent: RootDocument,
  notFoundComponent: NotFound,
  errorComponent: ErrorView,
})

// 紙の質感の画像があれば、レシートと紙の札に敷く
const PAPER = assetUrl('paper')

function RootDocument({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const locale = localeFromPathname(pathname)
  const fonts = fontsFor(locale)
  useFocusChime()
  return (
    <html lang={locale} style={PAPER ? ({ '--paper-tex': `url(${PAPER})` } as CSSProperties) : undefined}>
      <head>
        <HeadContent />
        {/* 和文フォントの CSS は大きいので、描画を止めないよう後から読み込む */}
        <script dangerouslySetInnerHTML={{ __html: fontLoader(fonts) }} />
        <noscript>
          <link rel="stylesheet" href={fonts} />
        </noscript>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function NotFound() {
  const { t, path } = useLocale()
  return (
    <>
      <title>{t.meta.notFoundTitle}</title>
      <SiteHeader variant="solid" />
      <main id="main" className="center-page">
        <div className="empty-seat">
          <LampMark size={88} lit={false} />
          <h1 className="empty-seat__title">{t.notFound.title}</h1>
          <p className="empty-seat__body">{t.notFound.body}</p>
          <Link to={path('/')} className="btn btn--ghost">
            {t.notFound.back}
          </Link>
        </div>
      </main>
    </>
  )
}

function ErrorView({ reset }: { reset: () => void }) {
  const { t } = useLocale()
  return (
    <main id="main" className="center-page">
      <div className="empty-seat">
        <LampMark size={88} lit={false} />
        <h1 className="empty-seat__title">{t.error.title}</h1>
        <p className="empty-seat__body">{t.error.body}</p>
        <button type="button" className="btn btn--ghost" onClick={reset}>
          {t.error.retry}
        </button>
      </div>
    </main>
  )
}
